import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { SearchAddon } from 'xterm-addon-search';
import {
  Box,
  Paper,
  IconButton,
  Toolbar,
  AppBar,
  Button,
  TextField,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  GetApp as DownloadIcon
} from '@mui/icons-material';
import apiService from '../services/api';
import 'xterm/css/xterm.css';

const VMConsoleViewer = ({ vmName }) => {
  const terminalRef = useRef(null);
  const terminal = useRef(null);
  const fitAddon = useRef(null);
  const searchAddon = useRef(null);
  const autoRefreshInterval = useRef(null);

  const [consoleOutput, setConsoleOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(5);
  const [lines, setLines] = useState(100);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // 터미널 초기화
  const initializeTerminal = useCallback(() => {
    if (terminal.current) {
      terminal.current.dispose();
    }

    terminal.current = new Terminal({
      cursorBlink: false,
      fontSize: 13,
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
      theme: {
        background: '#1e1e1e',
        foreground: '#d4d4d4',
        cursor: '#d4d4d4',
        selection: '#3e3e3e',
        black: '#000000',
        red: '#cd0000',
        green: '#00cd00',
        yellow: '#cdcd00',
        blue: '#0000ee',
        magenta: '#cd00cd',
        cyan: '#00cdcd',
        white: '#e5e5e5',
        brightBlack: '#7f7f7f',
        brightRed: '#ff0000',
        brightGreen: '#00ff00',
        brightYellow: '#ffff00',
        brightBlue: '#5c5cff',
        brightMagenta: '#ff00ff',
        brightCyan: '#00ffff',
        brightWhite: '#ffffff'
      },
      convertEol: true,
      scrollback: 10000,
      disableStdin: true, // 읽기 전용
      allowTransparency: false
    });

    // 애드온 추가
    fitAddon.current = new FitAddon();
    searchAddon.current = new SearchAddon();

    terminal.current.loadAddon(fitAddon.current);
    terminal.current.loadAddon(searchAddon.current);

    // DOM에 터미널 연결
    if (terminalRef.current) {
      terminal.current.open(terminalRef.current);
      fitAddon.current.fit();
    }

    return () => {
      if (terminal.current) {
        terminal.current.dispose();
      }
    };
  }, []);

  // 콘솔 출력 로드
  const loadConsoleOutput = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getVMConsole(vmName, lines);
      const output = response.data.console_output || '';

      setConsoleOutput(output);

      // 터미널 초기화 및 출력 표시
      if (terminal.current) {
        terminal.current.clear();

        if (output) {
          terminal.current.write(output);
        } else {
          terminal.current.write('\r\n콘솔 출력이 없습니다.\r\n');
          terminal.current.write('VM이 시리얼 콘솔을 사용하도록 설정되어 있는지 확인하세요.\r\n');
        }

        // 스크롤을 맨 아래로
        terminal.current.scrollToBottom();
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || '콘솔 출력을 불러오는데 실패했습니다.';
      setError(errorMessage);

      if (terminal.current) {
        terminal.current.clear();
        terminal.current.write(`\r\n\x1b[31m오류: ${errorMessage}\x1b[0m\r\n`);
      }
    } finally {
      setLoading(false);
    }
  }, [vmName, lines]);

  // 검색 기능
  const handleSearch = (direction = 'next') => {
    if (searchAddon.current && searchTerm) {
      if (direction === 'next') {
        searchAddon.current.findNext(searchTerm);
      } else {
        searchAddon.current.findPrevious(searchTerm);
      }
    }
  };

  // 터미널 크기 조정
  const resizeTerminal = useCallback(() => {
    if (fitAddon.current && terminal.current) {
      try {
        fitAddon.current.fit();
      } catch (error) {
        // 크기 조정 실패 무시
      }
    }
  }, []);

  // 전체화면 토글
  const toggleFullscreen = useCallback(async () => {
    try {
      if (!isFullscreen) {
        const element = document.documentElement;
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await element.webkitRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        }
      }
    } catch (error) {
      console.error('전체화면 전환 실패:', error);
    }
  }, [isFullscreen]);

  // 전체화면 상태 감지
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);

      // 터미널 크기 재조정
      setTimeout(() => resizeTerminal(), 50);
      setTimeout(() => resizeTerminal(), 200);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, [resizeTerminal]);

  // 콘솔 출력 다운로드
  const downloadConsoleOutput = () => {
    const blob = new Blob([consoleOutput], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${vmName}-console-${new Date().toISOString()}.log`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // 자동 새로고침 토글
  const handleAutoRefreshToggle = (enabled) => {
    setAutoRefresh(enabled);

    if (enabled) {
      // 즉시 한 번 로드
      loadConsoleOutput();

      // 자동 새로고침 시작
      autoRefreshInterval.current = setInterval(() => {
        loadConsoleOutput();
      }, refreshInterval * 1000);
    } else {
      // 자동 새로고침 중지
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
        autoRefreshInterval.current = null;
      }
    }
  };

  // 새로고침 간격 변경
  const handleRefreshIntervalChange = (interval) => {
    setRefreshInterval(interval);

    // 자동 새로고침이 활성화되어 있으면 재시작
    if (autoRefresh) {
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
      }

      autoRefreshInterval.current = setInterval(() => {
        loadConsoleOutput();
      }, interval * 1000);
    }
  };

  // 컴포넌트 마운트
  useEffect(() => {
    const cleanup = initializeTerminal();
    loadConsoleOutput();

    // 윈도우 크기 변경 시 터미널 크기 조정
    window.addEventListener('resize', resizeTerminal);

    return () => {
      window.removeEventListener('resize', resizeTerminal);

      // 자동 새로고침 정리
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
      }

      cleanup();
    };
  }, [initializeTerminal, loadConsoleOutput, resizeTerminal]);

  // 라인 수 변경 시 다시 로드
  useEffect(() => {
    if (terminal.current) {
      loadConsoleOutput();
    }
  }, [lines, loadConsoleOutput]);

  return (
    <Paper
      elevation={3}
      sx={{
        height: isFullscreen ? '100vh' : 600,
        width: isFullscreen ? '100vw' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#1e1e1e',
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 0 : 'auto',
        left: isFullscreen ? 0 : 'auto',
        zIndex: isFullscreen ? 9999 : 'auto'
      }}
    >
      {/* 헤더 */}
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar variant="dense" sx={{ minHeight: 48 }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            VM 콘솔: {vmName}
            <Typography variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
              (읽기 전용)
            </Typography>
          </Typography>

          {/* 설정 */}
          <FormControl size="small" sx={{ mr: 1, minWidth: 80 }}>
            <InputLabel>라인</InputLabel>
            <Select
              value={lines}
              onChange={(e) => setLines(e.target.value)}
              label="라인"
            >
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={200}>200</MenuItem>
              <MenuItem value={500}>500</MenuItem>
              <MenuItem value={1000}>1000</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => handleAutoRefreshToggle(e.target.checked)}
                size="small"
              />
            }
            label="자동 새로고침"
            sx={{ mr: 1 }}
          />

          {autoRefresh && (
            <FormControl size="small" sx={{ mr: 1, minWidth: 60 }}>
              <Select
                value={refreshInterval}
                onChange={(e) => handleRefreshIntervalChange(e.target.value)}
              >
                <MenuItem value={2}>2초</MenuItem>
                <MenuItem value={5}>5초</MenuItem>
                <MenuItem value={10}>10초</MenuItem>
                <MenuItem value={30}>30초</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* 제어 버튼 */}
          <IconButton
            onClick={() => setShowSearch(!showSearch)}
            size="small"
            title="검색"
          >
            <SearchIcon />
          </IconButton>

          <IconButton
            onClick={loadConsoleOutput}
            size="small"
            title="새로고침"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>

          <IconButton
            onClick={downloadConsoleOutput}
            size="small"
            title="다운로드"
            disabled={!consoleOutput}
          >
            <DownloadIcon />
          </IconButton>

          <IconButton
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "전체화면 해제" : "전체화면"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Toolbar>

        {/* 검색 바 */}
        {showSearch && (
          <Box sx={{ p: 1, bgcolor: 'grey.100' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="검색어 입력..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch('next');
                }
              }}
              InputProps={{
                endAdornment: (
                  <Box>
                    <Button size="small" onClick={() => handleSearch('previous')}>
                      이전
                    </Button>
                    <Button size="small" onClick={() => handleSearch('next')}>
                      다음
                    </Button>
                  </Box>
                )
              }}
            />
          </Box>
        )}
      </AppBar>

      {/* 오류 메시지 */}
      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadConsoleOutput}>
              재시도
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* 터미널 영역 */}
      <Box
        ref={terminalRef}
        sx={{
          flexGrow: 1,
          bgcolor: '#1e1e1e',
          '& .xterm': {
            height: '100% !important',
            width: '100% !important',
            padding: '10px'
          },
          '& .xterm-viewport': {
            overflow: 'auto !important'
          }
        }}
      />

      {/* 로딩 오버레이 */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 48,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Paper>
  );
};

export default VMConsoleViewer;
